import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Col, Container, Row, Spinner, Table } from 'reactstrap';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import { Redirect } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { uniqueId } from 'lodash';
import InfoBullItem from '../Switch/InfoBull';
import imgNoForms from '../../assets/img/no-forms.png';
import Translate, { t } from '../common/Translate';
import RecordButton from './RecordButton';
import RecordRow from './RecordRow';
import PaginationPage from '../Pagination/PaginationPage';
import SelectCheckboxes from './SelectCheckboxes';
import { autobizLeadsUrl } from '../../config';
import { Auth } from '../../config/configuration/react';

export default function RecordsResult(props) {
  const {
    error,
    loading,
    forms,
    origin,
    currentPage,
    pageNumbers,
    changePaginationIndex,
    setFiltersPanelOpen,
    filtersPanelOpen,
    showPaginnation,
    tableFilters,
    setSort,
    sort,
    currentTab,
  } = props;

  const [redirect, setRedirect] = useState(null);
  const country = Auth.country()?.toLowerCase();
  const linkAutobizLeads = `${autobizLeadsUrl}/${country}/debriefing?currentTab=debriefStatus4`;
  const [isOpen, setIsOpen] = useState(false);
  const clickOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="results-container">
      <Container
        fluid
        id="result"
        className={!isMobile && `overflow-auto ${loading || error || forms.length !== 0 ? 'container-min-height' : ''}`}
      >
        {redirect && <Redirect to={{ pathname: redirect }} />}
        {forms && !error && (
          <>
            {isMobile ? (
              <MobileView>
                <br />
                {Auth.hasRules(['autobizLeads.user']) && forms.length > 0 && currentTab === 'buyerAwaitingReturn' && (
                  <center>
                    <Col xs={12} sm={2} md={2} lg={2}>
                      <a
                        href={linkAutobizLeads}
                        className="btn btn-1 btn-block px-2 custom-padding"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="d-inline-block d-sm-none d-lg-inline-block ml-2">{t('debriefButton')}</span>
                      </a>
                    </Col>
                  </center>
                )}
                <Buttons
                  currentPage={currentPage}
                  pageNumbers={pageNumbers}
                  changePaginationIndex={changePaginationIndex}
                  records={forms}
                  origin={origin}
                  currentTab={currentTab}
                  setFiltersPanelOpen={setFiltersPanelOpen}
                  filtersPanelOpen={filtersPanelOpen}
                  showPaginnation={showPaginnation}
                />
              </MobileView>
            ) : (
              <BrowserView>
                <RecordTable
                  currentPage={currentPage}
                  pageNumbers={pageNumbers}
                  changePaginationIndex={changePaginationIndex}
                  records={forms}
                  origin={origin}
                  currentTab={currentTab}
                  showPaginnation={showPaginnation}
                  tableFilters={tableFilters}
                  setSort={setSort}
                  sort={sort}
                  isOpen={isOpen}
                  clickOpen={clickOpen}
                />
                {Auth.hasRules(['autobizLeads.user']) && forms.length > 0 && currentTab === 'buyerAwaitingReturn' && (
                  <center>
                    <Col xs={12} sm={2} md={2} lg={2}>
                      <a
                        href={linkAutobizLeads}
                        className="btn btn-1 btn-block px-2 custom-padding"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="d-inline-block d-sm-none d-lg-inline-block ml-2">{t('debriefButton')}</span>
                      </a>
                    </Col>
                  </center>
                )}
              </BrowserView>
            )}
          </>
        )}
        {!loading && !error && forms.length === 0 && (
          <Col className="text-center cta-block">
            <img src={imgNoForms} className="img-fluid my-4" alt="" />
            {origin === 'search' && isMobile && (
              <button type="button" className="btn btn-1" onClick={() => setFiltersPanelOpen(!filtersPanelOpen)}>
                {t('search_cta')}
              </button>
            )}
            {origin !== 'search' && !Auth.roleViewerAccess() && (
              <button type="button" onClick={() => setRedirect('/files/create')} className="btn cta-button btn-1">
                <Translate code="identify_VO" />
              </button>
            )}
          </Col>
        )}
        {loading && (
          <Col className="loader-full-screen main">
            <Spinner color="primary" />
          </Col>
        )}
        {error && (
          <p className="text-center text-danger">
            <Translate code={error} />
          </p>
        )}
      </Container>
    </div>
  );
}

RecordsResult.propTypes = {
  error: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  forms: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]).isRequired,
  origin: PropTypes.string,
  currentTab: PropTypes.string,
  currentPage: PropTypes.number,
  pageNumbers: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired,
  changePaginationIndex: PropTypes.func,
  setFiltersPanelOpen: PropTypes.func,
  filtersPanelOpen: PropTypes.bool,
  showPaginnation: PropTypes.bool,
  tableFilters: PropTypes.shape({
    buyers: PropTypes.oneOf({}),
    concessions: PropTypes.oneOf({}),
  }),
  setSort: PropTypes.func,
  sort: PropTypes.oneOfType([PropTypes.array]),
};

RecordsResult.defaultProps = {
  error: null,
  origin: '',
  currentTab: '',
  currentPage: 0,
  changePaginationIndex: PropTypes.func,
  setFiltersPanelOpen: PropTypes.func,
  filtersPanelOpen: null,
  showPaginnation: true,
  tableFilters: {},
  setSort: undefined,
  sort: undefined,
};

function Buttons({
  records,
  origin,
  queries,
  currentPage,
  pageNumbers,
  changePaginationIndex,
  setFiltersPanelOpen,
  filtersPanelOpen,
  showPaginnation,
  currentTab,
}) {
  const scrollToTop = () => {
    setFiltersPanelOpen(!filtersPanelOpen);
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <Row className="mt-2">
        {records.length > 0 &&
          records.map((record) => (
            <Col key={record.id} xs="12" md="6" xl="4">
              <RecordButton record={record} origin={origin} currentTab={currentTab} queries={queries} />
            </Col>
          ))}
      </Row>
      <Row id="pagination">
        <Col className="pagination">
          <PaginationPage
            currentPage={currentPage}
            pageNumbers={pageNumbers}
            changePaginationIndex={changePaginationIndex}
            pageRange={5}
            showPaginnation={showPaginnation}
          />
        </Col>
      </Row>
      <MobileView>
        {records && records.length > 0 && origin === 'search' && (
          <div className="text-center search-cta-fixed" id="search-cta-fixed">
            <button type="button" className="btn btn-1" onClick={() => scrollToTop()}>
              {t('search_cta')}
            </button>
          </div>
        )}
      </MobileView>
    </>
  );
}

Buttons.propTypes = {
  records: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  origin: PropTypes.string,
  currentTab: PropTypes.string,
  currentPage: PropTypes.number,
  pageNumbers: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
  changePaginationIndex: PropTypes.func.isRequired,
  setFiltersPanelOpen: PropTypes.func.isRequired,
  filtersPanelOpen: PropTypes.bool,
  queries: PropTypes.oneOfType([PropTypes.object]),
  showPaginnation: PropTypes.bool,
};

Buttons.defaultProps = {
  records: [],
  origin: '',
  currentTab: '',
  currentPage: 0,
  filtersPanelOpen: null,
  queries: {},
  showPaginnation: true,
};

function RecordTable({
  records,
  origin,
  queries,
  currentPage,
  pageNumbers,
  changePaginationIndex,
  showPaginnation,
  tableFilters,
  setSort,
  sort,
  currentTab,
  isOpen,
  clickOpen,
}) {
  const { buyers, concessions, sources } = tableFilters;
  const selectedPos = useSelector((state) => state.recordList.selectedPos);
  let tableHead = '';
  if (currentTab === 'supervisorCoverageRequest') {
    tableHead = (
      <thead>
        <tr>
          <th>
            <></>
          </th>
          <th>{t('#')}</th>
          <th>{t('make')}</th>
          <th>{t('model')}</th>
          <th>{t('year')}</th>
          <th>{t('mileage')}</th>
          <th onClick={() => setSort('coverageDate')}>
            {t('salesCreatedAt')}
            <FontAwesomeIcon
              icon={sort?.[0] === 'coverageDate' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
              className="sort-icon"
            />
          </th>
          <th>{t('coverageRequestTimer')}</th>
          <th>{t('countOffers')}</th>
          <th>{t('b2cMarketValue')}</th>
          <th>{t('bestSaleOfferAmount')}</th>
          <th>{t('rplCoverageRequest')}</th>
          <th>{t('currentWinner')}</th>
          <th>{t('carProfile')}</th>
        </tr>
      </thead>
    );
  } else if (['supervisorAwaiting', 'supervisorOngoingAppraisal'].includes(currentTab)) {
    tableHead = (
      <thead>
        <tr>
          <th>
            <></>
          </th>
          <th onClick={() => setSort('expertiseStartAt')}>
            {t('expertise_date')}
            <FontAwesomeIcon
              icon={sort?.[0] === 'expertiseStartAt' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
              className="sort-icon"
            />
          </th>
          <th>{t('#')}</th>

          <th>
            {origin === 'supervision' && (
              <SelectCheckboxes
                options={sources.options}
                placeholder={sources.placeholder}
                onChangeCallback={sources.setValue}
                allOption={sources.allOption}
                value={sources.value}
                inputValue={sources.inputValue}
                onInputChangeCallBack={sources.setInputValue}
              />
            )}
          </th>
          {currentTab === 'supervisorOngoingAppraisal' && <th>{t('status')}</th>}
          {currentTab !== 'supervisorOngoingAppraisal' && (
            <th onClick={() => setSort('profile')}>
              {t('carProfile')}&nbsp;
              <InfoBullItem
                tooltipOpen={isOpen}
                id="Tooltip-first"
                openInfoBull={() => clickOpen()}
                message="carProfileDescriptionBox"
                placement="top"
              />
              <FontAwesomeIcon
                icon={sort?.[0] === 'profile' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
                className="sort-icon"
              />
            </th>
          )}
          {currentTab === 'supervisorAwaiting' && <th>{t('supervisorsDashboardTimer')}</th>}
          <th>{t('b2cMarketValue')}</th>
          <th>{t('registration')}</th>
          <th>{t('makeModelYear')}</th>
          {origin === 'supervision' && (
            <>
              <th>
                <SelectCheckboxes
                  options={buyers.options}
                  placeholder={buyers.placeholder}
                  onChangeCallback={buyers.setValue}
                  allOption={buyers.allOption}
                  value={buyers.value}
                  inputValue={buyers.inputValue}
                  onInputChangeCallBack={buyers.setInputValue}
                />
              </th>

              <th>
                <SelectCheckboxes
                  options={concessions.options}
                  placeholder={concessions.placeholder}
                  onChangeCallback={concessions.setValue}
                  allOption={concessions.allOption}
                  value={concessions.value}
                  inputValue={concessions.inputValue}
                  onInputChangeCallBack={concessions.setInputValue}
                />
              </th>
            </>
          )}
        </tr>
      </thead>
    );
  } else if (
    [
      'onGoingNrm',
      'pricingInProgressNrm',
      'guaranteeActivatedNrm',
      'waitingCounterInspectionNrm',
      'finalizedNrm',
      'abandonedNrm',
    ].includes(currentTab)
  ) {
    tableHead = (
      <thead>
        <tr>
          <th>
            <></>
          </th>
          <th>{t('#')}</th>
          {!['onGoingNrm', 'pricingInProgressNrm'].includes(currentTab) && <th>{t('guaranteedId')}</th>}
          <th>{t('makeModelYear')}</th>
          <th>{t('mileage')}</th>
          <th>{t('registration')}</th>
          <th onClick={() => setSort('expertiseStartAt')}>
            {t('expertise_date')}
            <FontAwesomeIcon
              icon={sort?.[0] === 'expertiseStartAt' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
              className="sort-icon"
            />
          </th>
          {!['onGoingNrm', 'pricingInProgressNrm'].includes(currentTab) && (
            <th onClick={() => setSort('nrmStatusLabel')}>
              {t('nrmStatus')}
              <FontAwesomeIcon
                icon={sort?.[0] === 'nrmStatusLabel' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
                className="sort-icon"
              />
            </th>
          )}
          {origin === 'dashboardNrm' && (
            <>
              <th>
                <SelectCheckboxes
                  options={buyers.options}
                  placeholder={buyers.placeholder}
                  onChangeCallback={buyers.setValue}
                  allOption={buyers.allOption}
                  value={buyers.value}
                  inputValue={buyers.inputValue}
                  onInputChangeCallBack={buyers.setInputValue}
                />
              </th>
              <th>
                <SelectCheckboxes
                  options={concessions.options}
                  placeholder={concessions.placeholder}
                  onChangeCallback={concessions.setValue}
                  allOption={concessions.allOption}
                  value={concessions.value}
                  inputValue={concessions.inputValue}
                  onInputChangeCallBack={concessions.setInputValue}
                />
              </th>
            </>
          )}
        </tr>
      </thead>
    );
  } else {
    tableHead = (
      <thead>
        <tr>
          <th>
            <></>
          </th>
          <th>{t('#')}</th>
          {currentTab === 'buyerAwaitingPricing' && <th>{t('supervisorsDashboardTimer')}</th>}
          <th>{t('make')}</th>
          <th>{t('model')}</th>
          <th onClick={() => origin === 'search' && setSort('registrationDate')}>
            {t('year')}
            {origin === 'search' && (
              <FontAwesomeIcon
                icon={sort?.[0] === 'registrationDate' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
                className="sort-icon"
              />
            )}
          </th>
          <th onClick={() => origin === 'search' && setSort('mileage')}>
            {t('mileage')}
            {origin === 'search' && (
              <FontAwesomeIcon
                icon={sort?.[0] === 'mileage' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
                className="sort-icon"
              />
            )}
          </th>
          <th>{t('registration')}</th>
          <th onClick={() => origin === 'search' && setSort('expertiseStartAt')}>
            {t('expertise_date')}
            {origin === 'search' && (
              <FontAwesomeIcon
                icon={sort?.[0] === 'expertiseStartAt' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
                className="sort-icon"
              />
            )}
          </th>
          {selectedPos === 'privatePos' && (
            <th onClick={() => origin === 'search' && setSort('entryStockDate')}>
              {t('entryStockDate')}
              {origin === 'search' && (
                <FontAwesomeIcon
                  icon={sort?.[0] === 'entryStockDate' ? (sort?.[1] === 'DESC' && faSortDown) || faSortUp : faSort}
                  className="sort-icon"
                />
              )}
            </th>
          )}
          <th>{t('status')}</th>
        </tr>
      </thead>
    );
  }

  return (
    <>
      <Table hover className="table-records">
        {tableHead}
        <tbody>
          {records.length > 0 &&
            records.map((record) => (
              <RecordRow
                key={uniqueId(record.id)}
                record={record}
                origin={origin}
                currentTab={currentTab}
                queries={queries}
              />
            ))}
        </tbody>
      </Table>
      <Row>
        <Col className="pagination">
          <PaginationPage
            currentPage={currentPage}
            pageNumbers={pageNumbers}
            changePaginationIndex={changePaginationIndex}
            pageRange={10}
            showPaginnation={showPaginnation}
          />
        </Col>
      </Row>
    </>
  );
}

RecordTable.propTypes = {
  records: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  origin: PropTypes.string,
  currentTab: PropTypes.string,
  currentPage: PropTypes.number,
  pageNumbers: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
  changePaginationIndex: PropTypes.func.isRequired,
  queries: PropTypes.oneOfType([PropTypes.object]),
  showPaginnation: PropTypes.bool,
  tableFilters: PropTypes.shape({
    buyers: PropTypes.oneOf({}),
    concessions: PropTypes.oneOf({}),
    sources: PropTypes.oneOf({}),
  }),
  setSort: PropTypes.func,
  sort: PropTypes.oneOfType([PropTypes.array]),
  clickOpen: PropTypes.func,
  isOpen: PropTypes.bool,
};

RecordTable.defaultProps = {
  records: null,
  origin: '',
  currentTab: '',
  currentPage: 0,
  showPaginnation: true,
  queries: {},
  tableFilters: {},
  setSort: undefined,
  sort: undefined,
  clickOpen: undefined,
  isOpen: null,
};
